import { Viewer } from "@xeokit/xeokit-sdk";
import { useContext, useEffect, useRef } from "react";
import { ViewerContext } from "../../Context/ViewerContext/ViewerContextProvider";
import { XktContext } from "../../Context/XktContext/XktContextProvider";

const diff = (prev: string[], next: string[]) => {
    const prevSet = new Set(prev);
    const nextSet = new Set(next);
    return {
        added: next.filter((x) => !prevSet.has(x)),
        removed: prev.filter((x) => !nextSet.has(x)),
    };
};

const getSpecialElementIds = (viewer: Viewer | null) => {
    if (!viewer?.metaScene) return { gridElementIds: [], spaceElementIds: [] };

    return {
        gridElementIds: Object.keys(viewer.metaScene.metaObjectsByType["IfcGrid"] || {}),
        spaceElementIds: Object.keys(viewer.metaScene.metaObjectsByType["IfcSpace"] || {}),
    };
};

const useEntityMutations = () => {
    const { state: viewerState } = useContext(ViewerContext);
    const { state } = useContext(XktContext);

    const viewer = state.viewer; // stable shortcut
    const prevInvisibleRef = useRef<string[]>([]);
    const prevXrayedRef = useRef<string[]>([]);
    const prevSelectedRef = useRef<string[]>([]);
    const prevHighlightedRef = useRef<string[]>([]);
    const prevInvisibleAsXrayRef = useRef<boolean>(false);

    useEffect(() => {
        if (!viewer) return;

        const { gridElementIds } = getSpecialElementIds(viewer);

        viewer.scene.setObjectsVisible(gridElementIds, viewerState.gridEnabled);
        viewer.scene.setObjectsPickable(gridElementIds, !viewerState.gridEnabled);
    }, [viewerState.gridEnabled, viewer]);

    useEffect(() => {
        if (!viewer) return;

        const { spaceElementIds } = getSpecialElementIds(viewer);

        viewer.scene.setObjectsVisible(spaceElementIds, viewerState.spaceEnabled);
        viewer.scene.setObjectsPickable(spaceElementIds, !viewerState.spaceEnabled);
    }, [viewerState.spaceEnabled, viewer]);

    useEffect(() => {
        if (!viewer) return;

        const invisibleAsXray = viewerState.invisibleAsXray;

        const prevInvisible = prevInvisibleRef.current;
        const nextInvisible = viewerState.invisibleEntities;
        const { added: invAdded, removed: invRemoved } = diff(prevInvisible, nextInvisible);

        const prevXrayed = prevXrayedRef.current;
        const nextXrayed = viewerState.xrayedEntities;
        const { added: xrAdded, removed: xrRemoved } = diff(prevXrayed, nextXrayed);

        const invisModeChanged = invisibleAsXray !== prevInvisibleAsXrayRef.current;
        prevInvisibleAsXrayRef.current = invisibleAsXray;

        if (invisModeChanged) {
            if (prevXrayed.length > 0) {
                viewer.scene.setObjectsXRayed(prevXrayed, false);
                viewer.scene.setObjectsPickable(prevXrayed, true);
            }
        }

        if (invRemoved.length > 0) {
            viewer.scene.setObjectsXRayed(invRemoved, false);
            viewer.scene.setObjectsVisible(invRemoved, true);
            viewer.scene.setObjectsPickable(invRemoved, true);
        }

        if (invAdded.length > 0) {
            if (invisibleAsXray) {
                viewer.scene.setObjectsXRayed(invAdded, true);
                viewer.scene.setObjectsVisible(invAdded, true);
                viewer.scene.setObjectsPickable(invAdded, false);
            } else {
                viewer.scene.setObjectsVisible(invAdded, false);
                viewer.scene.setObjectsXRayed(invAdded, false);
                viewer.scene.setObjectsPickable(invAdded, true);
            }
        }

        prevInvisibleRef.current = [...nextInvisible];

        if (!invisibleAsXray) {
            if (xrRemoved.length > 0) {
                viewer.scene.setObjectsXRayed(xrRemoved, false);
                viewer.scene.setObjectsPickable(xrRemoved, true);
            }

            if (xrAdded.length > 0) {
                viewer.scene.setObjectsXRayed(xrAdded, true);
                viewer.scene.setObjectsVisible(xrAdded, true);
                viewer.scene.setObjectsPickable(xrAdded, false);
            }
        } else {
            viewer.scene.setObjectsXRayed(nextXrayed, false);
        }

        prevXrayedRef.current = [...nextXrayed];
    }, [viewer, viewerState.invisibleEntities, viewerState.xrayedEntities, viewerState.invisibleAsXray]);

    useEffect(() => {
        if (!viewer) return;

        const prevSelected = prevSelectedRef.current;
        const nextSelected = viewerState.selectedEntities;

        const { added, removed } = diff(prevSelected, nextSelected);

        if (removed.length > 0) {
            viewer.scene.setObjectsSelected(removed, false);
        }
        if (added.length > 0) {
            viewer.scene.setObjectsSelected(added, true);
            viewer.scene.setObjectsPickable(added, true);
        }

        prevSelectedRef.current = [...nextSelected];
    }, [viewerState.selectedEntities, viewer]);

    useEffect(() => {
        if (!viewer) return;

        const prevHighlighted = prevHighlightedRef.current;
        const nextHighlighted = viewerState.highlightedEntities;

        const { added, removed } = diff(prevHighlighted, nextHighlighted);

        if (removed.length > 0) {
            viewer.scene.setObjectsHighlighted(removed, false);
        }
        if (added.length > 0) {
            viewer.scene.setObjectsHighlighted(added, true);
        }

        prevHighlightedRef.current = [...nextHighlighted];
    }, [viewerState.highlightedEntities, viewer]);
};

export default useEntityMutations;
